// Signup.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../CSS/signup.css";
import Swal from "sweetalert2";
import logo from "../images/logo.png";
import { BASE_API_URL } from "../constants";

// ── BTS Images: place bts1.jpg … bts35.jpg inside public/bts/ ────────────────
const BTS_IMAGES = Array.from({ length: 35 }, (_, i) => `/bts/bts-${i + 1}.jpg`);
// ─────────────────────────────────────────────────────────────────────────────

const Signup = () => {
  const navigate = useNavigate();

  // ── Full-page BTS background cycling ─────────────────────────────────────
  const [bgIndex, setBgIndex] = useState(0);
  const [bgVisible, setBgVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setBgVisible(false);
      setTimeout(() => {
        setBgIndex((prev) => (prev + 1) % BTS_IMAGES.length);
        setBgVisible(true);
      }, 700);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    mobilenumber: "",
    username: "",
    password: "",
    confirmPassword: "",
    referralCode: "",
    terms: false,
  });

  useEffect(() => {
    const urlRef = new URLSearchParams(window.location.search).get("ref");
    const storedRef = sessionStorage.getItem("referralCode");
    const refCode = urlRef || storedRef;

    if (refCode) {
      setFormData((prev) => ({ ...prev, referralCode: refCode }));
      sessionStorage.setItem("referralCode", refCode);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.mobilenumber) {
      Swal.fire({
        title: "Missing Field",
        text: "Mobile number is required!",
        icon: "warning",
        confirmButtonColor: "#ff8906",
      });
      return;
    }

    const password = formData.password;
    const passwordConditions = [
      { test: password.length >= 8, message: "Password must be at least 8 characters long." },
      { test: /[A-Z]/.test(password), message: "Password must contain at least one uppercase letter." },
      { test: /[a-z]/.test(password), message: "Password must contain at least one lowercase letter." },
      { test: /\d/.test(password), message: "Password must contain at least one number." },
      { test: /[@$!%*?&#]/.test(password), message: "Password must contain at least one special character (@$!%*?&#)." },
    ];

    for (let condition of passwordConditions) {
      if (!condition.test) {
        Swal.fire({
          title: "Weak Password",
          text: condition.message,
          icon: "warning",
          confirmButtonColor: "#ff8906",
        });
        return;
      }
    }

    if (password !== formData.confirmPassword) {
      Swal.fire({
        title: "Password Mismatch",
        text: "Passwords do not match!",
        icon: "error",
        confirmButtonColor: "#e53170",
      });
      return;
    }

    try {
      const formBody = new FormData();
      formBody.append("firstname", formData.firstname);
      formBody.append("lastname", formData.lastname);
      formBody.append("email", formData.email);
      formBody.append("mobilenumber", formData.mobilenumber);
      formBody.append("username", formData.username);
      formBody.append("password", formData.password);
      if (formData.referralCode) formBody.append("referralCode", formData.referralCode);
      formBody.append("terms", formData.terms);

      const response = await fetch(`${BASE_API_URL}/api/contributor-signup`, {
        method: "POST",
        body: formBody,
      });

      const data = await response.json();

      if (response.ok) {
        const newReferralCode = data.referralCode;
        let successText = "Redirecting to login page...";
        if (newReferralCode) {
          successText = `Account created! Your unique Referral Code is: ${newReferralCode}. Share the link to earn 50! Redirecting to login...`;
        }
        Swal.fire({
          title: "Signup Successful ",
          text: successText,
          icon: "success",
          showConfirmButton: false,
          timer: 4000,
          timerProgressBar: true,
        });
        setTimeout(() => navigate("/login"), 4000);
      } else {
        Swal.fire({
          title: "Signup Failed",
          text: data.detail || "Something went wrong!",
          icon: "error",
          confirmButtonColor: "#e53170",
        });
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err.message,
        icon: "error",
        confirmButtonColor: "#e53170",
      });
    }
  };

  return (
    <div className="signup-page">

      {/* ── Full-page BTS Background Image ── */}
      <div
        className="page-bg"
        style={{
          backgroundImage: `url(${BTS_IMAGES[bgIndex]})`,
          opacity: bgVisible ? 1 : 0,
        }}
      />
      {/* Dark cinematic overlay */}
      <div className="page-bg-overlay" />

      {/* ── Left Panel (hidden on mobile) ── */}
      <div className="film-section">
        <div className="film-content">
          <img src={logo} alt="Logo" className="logo-2" />
          <h1 className="film-title">Where Creativity Meets Contributions</h1>
          <p className="film-subtitle">
            The premier platform connecting passionate contributors with groundbreaking cinematic projects. Join our community to bring movies to life.
          </p>

          <div className="features">
            <div className="feature">
              <div className="feature-icon">
                <i className="fas fa-film"></i>
              </div>
              <div className="feature-text">
                <h3>For Contributors</h3>
                <p>Discover promising movie projects and track your contributions</p>
              </div>
            </div>
            <div className="feature">
              <div className="feature-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="feature-text">
                <h3>Movie Projects</h3>
                <p>Explore a wide variety of cinematic projects and be part of their success</p>
              </div>
            </div>
            <div className="feature">
              <div className="feature-icon">
                <i className="fas fa-network-wired"></i>
              </div>
              <div className="feature-text">
                <h3>Global Network</h3>
                <p>Connect with professionals worldwide in the film industry</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right Panel — Signup Form ── */}
      <div className="auth-container">
        <div className="auth-content">
          <div className="auth-header">
            <h2 className="auth-title">Join Our Community</h2>
            <p className="auth-subtitle">Create your Cinevestor account</p>
          </div>
          <div className="user-type-toggle">
            <div className="user-option investor active">
              <i className="fas fa-hand-holding-usd"></i>
              <div>Contributor</div>
            </div>
          </div>
          <form className="auth-form" id="signup-form" onSubmit={handleSubmit}>
            <div className="name-group">
              <div className="input-group">
                <label htmlFor="firstname">First Name</label>
                <input
                  type="text"
                  id="firstname"
                  name="firstname"
                  placeholder="First Name"
                  required
                  value={formData.firstname}
                  onChange={handleChange}
                />
                <i className="fas fa-user" style={{ marginTop: "9px" }}></i>
              </div>

              <div className="input-group">
                <label htmlFor="lastname">Last Name</label>
                <input
                  type="text"
                  id="lastname"
                  name="lastname"
                  placeholder="Last Name"
                  required
                  value={formData.lastname}
                  onChange={handleChange}
                />
                <i className="fas fa-user" style={{ marginTop: "10px" }}></i>
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email"
                required
                value={formData.email}
                onChange={handleChange}
              />
              <i className="fas fa-envelope" style={{ marginTop: "10px" }}></i>
            </div>

            <div className="input-group">
              <label htmlFor="mobilenumber">Mobile Number</label>
              <input
                type="tel"
                id="mobilenumber"
                name="mobilenumber"
                placeholder="Enter your mobile number"
                required
                value={formData.mobilenumber}
                onChange={handleChange}
              />
              <i className="fas fa-phone" style={{ marginTop: "10px" }}></i>
            </div>

            <div className="input-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                name="username"
                placeholder="Enter your Username"
                required
                value={formData.username}
                onChange={handleChange}
              />
              <i className="fas fa-shield-alt" style={{ marginTop: "10px" }}></i>
            </div>

            <div className="input-group">
              <label htmlFor="referralCode">Referral Code (Optional)</label>
              <input
                type="text"
                id="referralCode"
                name="referralCode"
                placeholder="Enter referral code if you have one"
                value={formData.referralCode}
                onChange={handleChange}
              />
              <i className="fas fa-gift" style={{ marginTop: "10px" }}></i>
            </div>

            <div className="input-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                placeholder="Create a password"
                required
                value={formData.password}
                onChange={handleChange}
              />
              <i className="fas fa-lock" style={{ marginTop: "10px" }}></i>
            </div>

            <div className="input-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                placeholder="Confirm your password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
              />
              <i className="fas fa-lock" style={{ marginTop: "10px" }}></i>
            </div>

            <div className="terms">
              <input
                type="checkbox"
                id="terms"
                name="terms"
                checked={formData.terms}
                onChange={handleChange}
                required
              />
              <label htmlFor="terms">
                I agree to the{" "}
                <a href="/Terms" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="/Privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
              </label>
            </div>

            <button type="submit" className="auth-button">
              Create Account
            </button>

            <div className="auth-footer">
              Already have an account?{" "}
              <Link to="/login" style={{ textDecoration: "none" }}>
                Log in
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
